import React, { useContext, useState, useContext as useContextReact } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../hooks/ThemeContext';
import { api } from '../services/baseUrl';
import { CartContext } from '../hooks/CartContext';
import { formatCurrency } from '../utils/formatters';
import LoadingSpinner from '../components/globals/LoadingSpinner';
import SectionTitle from '../components/common/SectionTitle';

const Checkout = () => {
  const { theme } = useContext(ThemeContext);
  const { cart } = useContextReact(CartContext);
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
    const [paymentMode, setPaymentMode] = useState("one_time"); // 'one_time' ou 'subscription'

    const handleStripeCheckout = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post("/payment/create-checkout-session", {
          shipping_address: null,
          billing_address: null,
          payment_mode: paymentMode, // Ajout du mode de paiement
        });

        if (response.data?.success && response.data?.session_url) {
          window.location.href = response.data.session_url;
          return;
        }

        setError(
          response.data?.message || "Impossible de démarrer le paiement",
        );
      } catch (err) {
        const message =
          err.response?.data?.message || err.message || "Erreur paiement";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

  // Calcul de la taxe 0.25% sur le total du panier
  const taxRate = 0.0025;
  const subtotal = cart?.total || 0;
  const taxAmount = subtotal * taxRate;
  const totalWithTax = subtotal + taxAmount;

  return (
    <div
      className={`flex-1 flex flex-col ${isDark ? "bg-[#0f1114] text-white" : "bg-slate-50 text-gray-800"}`}
    >
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <SectionTitle
          title="Paiement"
          subtitle="Redirection vers la page de paiement sécurisée"
          align="left"
        />

        {error && (
          <div
            className={`p-4 rounded-lg mb-6 ${isDark ? "bg-red-500/20 text-red-300 border border-red-500/30" : "bg-red-50 text-red-800 border border-red-200"}`}
          >
            {error}
          </div>
        )}

        <div
          className={`p-6 rounded-lg ${isDark ? "bg-[#0b0b0c] border border-white/6" : "bg-white border border-gray-100"}`}
        >
          <div className="mb-6">
            <label className="block font-semibold mb-2">
              Choisissez votre mode de paiement :
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="paymentMode"
                  value="one_time"
                  checked={paymentMode === "one_time"}
                  onChange={() => setPaymentMode("one_time")}
                />
                Paiement comptant (paiement unique)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="paymentMode"
                  value="subscription"
                  checked={paymentMode === "subscription"}
                  onChange={() => setPaymentMode("subscription")}
                />
                Abonnement 80 €/mois sur 3 ans
              </label>
            </div>
          </div>
          <p
            className={`text-sm mb-6 ${isDark ? "text-white/70" : "text-gray-600"}`}
          >
            Paiement sécurisé par Stripe. Si une promotion est active, elle est
            appliquée automatiquement aux produits éligibles.
            <br />
            <span className="font-semibold text-rose-500">
              Une taxe de 0,25&nbsp;% est appliquée sur le montant total.
            </span>
          </p>

          <div className="mb-4 space-y-1">
            <div className="flex justify-between text-base">
              <span>Sous-total</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-base">
              <span>Taxe (0,25&nbsp;%)</span>
              <span>{formatCurrency(taxAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold mt-2">
              <span>Total à payer</span>
              <span className="text-rose-600">
                {formatCurrency(totalWithTax)}
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleStripeCheckout}
              disabled={loading}
              className="px-5 py-3 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700 transition-colors disabled:opacity-50"
            >
              {loading ? "Redirection..." : "Payer maintenant"}
            </button>
            <button
              onClick={() => navigate("/cart")}
              className={`px-5 py-3 rounded-lg border font-medium transition-colors ${
                isDark
                  ? "border-white/20 text-white hover:bg-white/10"
                  : "border-gray-300 text-gray-800 hover:bg-gray-50"
              }`}
            >
              Retour au panier
            </button>
          </div>

          {loading && (
            <div className="mt-6">
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
